/**
 * A single workflow library card — PURE (props in, JSX out). No Convex hooks,
 * no navigation: the parent route wires data + intent through callbacks.
 *
 * Ported from the `LibraryCard` visual in `WorkflowsMockup.tsx`, with the
 * demo-only scheduling surface removed: no `cadence` field, no `CalendarClock`
 * badge, no schedule strings, no "Next:" text. The bottom line is now a
 * data-derived last-run summary (relative recency + display state) instead of
 * the mock cadence row.
 *
 * The `active` badge is derived by the domain (`deriveSpecActivityLabel`) and
 * the last-run display state by `deriveExecutionDisplayState` — both single-
 * sourced so the library reads the same axes the per-thread tabs do. This is
 * the coarse library projection: no per-card bead join, so `needsUserBeads`
 * is passed `false` (workflows-dedicated-ui §3.2).
 */

import { BookOpen } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { StatusPill } from "@/components/dashboard/IntegrationCardShell";
import {
  deriveExecutionDisplayState,
  isParkedSnapshot,
  type ExecutionDisplayState,
  type ExecutionStatus,
} from "@domain/praxis-executions";
import { deriveSpecActivityLabel } from "@domain/praxis-specs";

/** The minimal spec projection a card renders — a subset of `Doc<"specs">`. */
export interface WorkflowCardSpec {
  readonly _id: string;
  readonly title: string;
  readonly active?: boolean;
  readonly userPaused?: boolean;
  readonly threadId?: string;
  /**
   * Optional one-line summary. The current `specs` projection carries no
   * description column, so this is absent in production today — the card
   * simply omits the line. Kept on the prop so a future projection field
   * (or a caller-supplied summary) lights it up without a component change.
   */
  readonly description?: string;
}

/** The minimal execution projection a card renders — a subset of `Doc<"executions">`. */
export interface WorkflowCardExecution {
  readonly status: ExecutionStatus;
  readonly startedAt: number;
  readonly completedAt?: number;
  readonly parked?: {
    readonly kind: "human" | "timer" | "event";
    readonly dueAt: number | null;
    readonly parkedAt: number;
    readonly beads: readonly unknown[];
  } | null;
}

export interface WorkflowCardProps {
  readonly spec: WorkflowCardSpec;
  readonly latestExecution: WorkflowCardExecution | null;
  /** Fired with the spec id when the card is opened. Navigation lives in the route. */
  readonly onOpen: (specId: string) => void;
  /** Index into the library grid — drives the deterministic icon/tone rotation. */
  readonly index?: number;
}

// A small deterministic palette so cards look intentional without per-spec
// authored icons. Every entry preserves its `dark:` variants verbatim from
// the mockup tones (surface-dark tokens defined in index.css).
const TONES: readonly string[] = [
  "bg-home-terracotta/12 dark:bg-home-terracotta/10 text-home-terracotta",
  "bg-home-sky/25 dark:bg-home-sky/10 text-home-water",
  "bg-home-candy/15 dark:bg-home-candy/10 text-home-candy",
  "bg-accent-green/12 text-accent-green",
  "bg-home-peri/30 dark:bg-home-peri/15 text-[#5B4F9E] dark:text-[#B7ADED]",
];

/**
 * Short label for the last-run display state (no "failed"/"held" members).
 * `parked` is the non-human base label — "held for you" phrasing belongs ONLY
 * to `parked.kind === "human"` (praxis-doctrine §10.9: timer/event parks are
 * healthy waits, not user-directed holds); `lastRunLine` overrides per kind.
 */
const DISPLAY_STATE_LABEL: Record<ExecutionDisplayState, string> = {
  running: "Running",
  parked: "Waiting",
  needs_attention: "Needs you",
  completed: "Completed",
  abandoned: "Stopped",
};

function relativeRecency(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Title fallback: specs carry a real `title`, but a projection row whose title
 * is still the raw `<threadId>.spec.md` filename (or empty) degrades to a
 * readable thread-stem instead of showing the filename.
 */
function displayTitle({ spec }: { spec: WorkflowCardSpec }): string {
  const raw = spec.title?.trim();
  if (raw && !raw.endsWith(".spec.md")) return raw;
  const stem = raw ? raw.replace(/\.spec\.md$/, "") : spec.threadId ?? spec._id;
  return `Workflow ${stem.slice(0, 6)}`;
}

/** The last-run line: never-run, or relative recency + coarse display state. */
function lastRunLine({
  latestExecution,
}: {
  latestExecution: WorkflowCardExecution | null;
}): { text: string; muted: boolean } {
  if (latestExecution === null) {
    return { text: "Never run", muted: true };
  }
  // Runtime-narrow the projection's loose `parked` field instead of casting —
  // the guard is the domain's single source for the snapshot shape.
  const parked = isParkedSnapshot(latestExecution.parked)
    ? latestExecution.parked
    : null;
  const state = deriveExecutionDisplayState({
    status: latestExecution.status,
    parked,
    needsUserBeads: false,
  });
  const label =
    state === "parked" && parked?.kind === "human"
      ? "Paused for you"
      : DISPLAY_STATE_LABEL[state];
  const when = relativeRecency(
    latestExecution.completedAt ?? latestExecution.startedAt,
  );
  return { text: `${label} · ${when}`, muted: false };
}

export function WorkflowCard({
  spec,
  latestExecution,
  onOpen,
  index = 0,
}: WorkflowCardProps) {
  const Icon: LucideIcon = BookOpen;
  const tone = TONES[index % TONES.length]!;
  const activityLabel = deriveSpecActivityLabel({
    active: spec.active,
    userPaused: spec.userPaused,
  });
  const title = displayTitle({ spec });
  const lastRun = lastRunLine({ latestExecution });

  return (
    <button
      type="button"
      onClick={() => onOpen(spec._id)}
      className="flex flex-col rounded-3xl border border-home-ink/8 dark:border-white/8 bg-white dark:bg-surface-dark-card p-5 text-left shadow-[0_16px_40px_-26px_rgba(33,31,28,0.4)] transition-transform hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between">
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tone}`}>
          <Icon className="h-5 w-5" strokeWidth={2.2} />
        </span>
        {activityLabel === "Live" ? (
          <StatusPill variant="active">Live</StatusPill>
        ) : (
          <StatusPill variant="muted">{activityLabel}</StatusPill>
        )}
      </div>
      <h3 className="mt-4 font-display text-[18px] font-semibold tracking-tight text-home-ink dark:text-white">
        {title}
      </h3>
      {spec.description ? (
        <p className="mt-1 line-clamp-2 font-body text-[13px] leading-relaxed text-home-ink/55 dark:text-white/55">
          {spec.description}
        </p>
      ) : null}
      <div className="mt-auto flex items-center gap-2 border-t border-home-ink/8 dark:border-white/8 pt-3 font-body text-[12px]">
        <span
          className={`ml-auto font-medium ${
            lastRun.muted
              ? "text-home-ink/40 dark:text-white/40"
              : "text-home-ink/60 dark:text-white/60"
          }`}
        >
          {lastRun.text}
        </span>
      </div>
    </button>
  );
}
